import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import {
  rmnDiscoveredBrandsReviewEndpoint,
  rmnIgnoreDiscoveredBrandEndpoint,
} from '../../api/endpoints'
import { MainContainer } from '../../components/ContentWrapper'
import { ListPageHeader } from '../../components/ListPageHeader/ListPageHeader'
import { CatalogTabs, BRAND_CATALOG_TABS } from './CatalogTabs'
import { ConfirmationDialog } from '../../components/ConfirmationDialog/ConfirmationDialog'
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable'
import { useCommonParams } from '../../hooks/useCommonParams'
import { useResourceList } from '../../hooks/useResourceList'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import { initialsFromName } from '../../utils'
import { RmnDiscoveredBrand } from '../../types/rmn'
import { ImportDiscoveredModal } from './ImportDiscoveredModal'
import {
  InNetworkPill,
  LinkedMatchNote,
  STATUS_FILTERS,
  StatusPill,
  isActionable,
} from './discoveredImportShared'

// Catalog › Brands › Discovered (TDC-1499 phase 2, RMN networks only):
// review the brands the analysis found on the retailer's site and add them
// to the catalog as network supplier brands (brands_rmn_brands). The server
// dedupes by exact name and creates unpublished brands otherwise. Reports
// and scores live on the Catalog tab — these rows aren't in the catalog yet,
// so there's nothing here to score.

const PAGE_SIZE = 25

export default function NetworkBrandsImportPage() {
  const navigate = useNavigate()
  const { isNetwork: isRmnUser, basePath, domain } = useNetworkScope()
  const analysisLabel = domain ? `${domain} analysis` : 'your catalog analysis'
  const {
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    page,
    setPage,
    getFilter,
    setFilter,
  } = useCommonParams()
  const [searchInput, setSearchInput] = useState(searchText)
  const [count, setCount] = useState(0)
  const [importing, setImporting] = useState<RmnDiscoveredBrand | null>(null)
  const [pendingIgnore, setPendingIgnore] = useState<RmnDiscoveredBrand | null>(null)
  const [ignoring, setIgnoring] = useState(false)

  const status = getFilter('status', 'pending')

  useEffect(() => {
    if (searchInput === searchText) return undefined
    const t = setTimeout(() => setSearchText(searchInput), 350)
    return () => clearTimeout(t)
  }, [searchInput]) // searchText intentionally omitted — sync effect below owns it

  useEffect(() => {
    if (searchText !== searchInput) setSearchInput(searchText)
  }, [searchText]) // searchInput intentionally omitted — debounce effect above owns it

  const list = useResourceList<RmnDiscoveredBrand>({
    endpoint: rmnDiscoveredBrandsReviewEndpoint,
    pageSize: PAGE_SIZE,
    errorLabel: 'Failed to load discovered brands',
    searchText,
    searchParams,
    setSearchParams,
    page,
    setPage,
    onCountChange: setCount,
    extraParams: { match_status: status },
  })

  if (!isRmnUser) return <Navigate to={`${basePath}/products`} replace />

  const handleIgnore = async () => {
    if (!pendingIgnore) return
    const label = pendingIgnore.name
    setIgnoring(true)
    try {
      await axiosInstant.post(rmnIgnoreDiscoveredBrandEndpoint(pendingIgnore.id))
      toast.success(`Ignored "${label}"`)
      setPendingIgnore(null)
      await list.initLoad(1)
    } catch {
      toast.error(`Couldn't ignore "${label}". Please try again.`)
    } finally {
      setIgnoring(false)
    }
  }

  const columns: DataTableColumn<RmnDiscoveredBrand>[] = [
    {
      key: 'brand',
      header: 'Brand',
      render: (b) => (
        <div className="flex items-center gap-[10px]">
          <div className="w-[30px] h-[30px] rounded-[7px] bg-tint text-accent-text text-[11px] font-bold flex items-center justify-center shrink-0 overflow-hidden">
            {b.logo ? (
              <img src={b.logo} alt="" className="w-full h-full object-contain" />
            ) : (
              initialsFromName(b.name)
            )}
          </div>
          <span className="font-semibold text-text-1">{b.name}</span>
        </div>
      ),
    },
    {
      key: 'url',
      header: 'URL',
      render: (b) =>
        b.url ? (
          <a
            href={b.url}
            target="_blank"
            rel="noreferrer"
            className="text-text-2 hover:underline truncate inline-block max-w-[260px] align-middle"
          >
            {b.url}
          </a>
        ) : (
          '—'
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (b) => <StatusPill status={b.match_status} />,
    },
    {
      key: 'matched',
      header: 'Matched',
      render: (b) =>
        b.matched_brand ? (
          <div className="flex items-center gap-2 flex-wrap">
            <LinkedMatchNote name={b.matched_brand.name} />
            {b.in_network && <InNetworkPill />}
          </div>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: 260,
      render: (b) =>
        isActionable(b.match_status) ? (
          <div className="flex items-center gap-1 justify-end">
            <button
              type="button"
              title={
                b.matched_brand
                  ? `Links this discovered brand to ${b.matched_brand.name} — won't create a duplicate`
                  : 'Import this brand into your network'
              }
              onClick={() => setImporting(b)}
              className={
                // Matched + already in network: importing is a no-op relink —
                // demote to the outlined style so the gold CTA means "adds
                // something new".
                b.matched_brand && b.in_network
                  ? 'text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2'
                  : 'text-[12px] font-semibold text-white bg-accent rounded-md px-2.5 py-1.5 border-0 cursor-pointer hover:bg-[#8A691F]'
              }
            >
              {b.matched_brand ? 'Link' : 'Import'}
            </button>
            <button
              type="button"
              title="Ignore — won't appear in future reviews"
              onClick={() => setPendingIgnore(b)}
              className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2"
            >
              Ignore
            </button>
          </div>
        ) : null,
    },
  ]

  const showCrawlEmptyState =
    status === 'pending' &&
    !searchText &&
    !list.loading &&
    !list.error &&
    count === 0

  const statusChips = (
    <div className="flex items-center gap-1.5 flex-wrap">
      {STATUS_FILTERS.map((f) => {
        const active = status === f.value
        return (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter('status', f.value === 'pending' ? null : f.value)}
            className={
              active
                ? 'text-[12px] font-semibold text-white bg-accent rounded-full px-3 py-1.5 border-0 cursor-pointer'
                : 'text-[12px] font-semibold text-text-2 border border-border-token rounded-full px-3 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2'
            }
          >
            {f.label}
          </button>
        )
      })}
    </div>
  )

  return (
    <MainContainer>
      <button
        type="button"
        onClick={() => navigate(`${basePath}/catalog/brands`)}
        className="text-[13px] font-semibold text-text-2 bg-transparent border-0 cursor-pointer hover:text-text-1 mb-3 px-0"
      >
        ← Back to Catalog
      </button>
      <ListPageHeader
        title="Discovered Brands"
        subtitle={
          count > 0
            ? `Brands discovered by ${analysisLabel} · ${count} total`
            : `Brands discovered by ${analysisLabel}`
        }
        tabs={<CatalogTabs tabs={BRAND_CATALOG_TABS} />}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchInputId="search-discovered-brands"
        searchPlaceholder="Search by name…"
        searchRightContent={statusChips}
      />

      {showCrawlEmptyState ? (
        <div className="max-w-[560px] text-[14px] text-text-2 leading-relaxed py-10">
          <p className="font-semibold text-text-1 mb-2">No brands to review</p>
          <p>
            Brands appear here once your catalog has been analyzed. Contact your
            Desire Company account manager to set up an analysis for your brand.
          </p>
        </div>
      ) : (
        <DataTable<RmnDiscoveredBrand>
          columns={columns}
          rows={list.items}
          loading={list.loading}
          totalCount={list.totalCount}
          hasMore={list.hasMore}
          error={list.error}
          onLoadMore={list.loadMore}
          emptyMessage="No discovered brands match this view"
          getRowKey={(r) => r.id}
        />
      )}

      {importing && (
        <ImportDiscoveredModal
          mode="brand"
          record={importing}
          onClose={() => setImporting(null)}
          onImported={async () => {
            setImporting(null)
            await list.initLoad(1)
          }}
        />
      )}

      {pendingIgnore && (
        <ConfirmationDialog
          open
          title="Ignore Brand"
          description={`Ignore "${pendingIgnore.name}"? Ignored brands won't appear in future reviews and won't be imported.`}
          confirmLabel="Ignore"
          onClose={() => setPendingIgnore(null)}
          onConfirm={handleIgnore}
          disabled={ignoring}
        />
      )}
    </MainContainer>
  )
}
